import {
    faAlarmClock,
    faArrowRotateRight,
    faArrowUpFromBracket,
    faCheck,
    faCopy,
    faPen,
    faSplit,
    faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { formatCadenceCompact, fromToolArgs } from './automations/scheduleFormat.ts';
import { ChatAlert } from './ChatAlert.tsx';
import { AttachmentChip } from './ChatAttachment.tsx';
import { ConversationFeedback } from './ConversationFeedback.tsx';
import { GeneratedFileCard } from './GeneratedFileCard.tsx';
import { MessageChart } from './MessageChart.tsx';
import {
    chartImageFileName,
    extractCharts,
    RENDERED_CHART_SOURCE,
    extractReferencedEmailIds,
    extractSuggestions,
    extractWorkbenchLinks,
    hasDetailedOptions,
    MARKDOWN_STYLES,
    renderMarkdown,
} from './messageContent.ts';
import { OptionCards } from './OptionCards.tsx';
import { PendingActionCard } from './PendingActionCard.tsx';
import {
    CREATED_DOCUMENT_META,
    extractCreatedDocuments,
    extractCreatedSchedules,
    extractGeneratedAttachmentIds,
    partitionToolCalls,
    parseToolOutput,
} from './toolDisplay.tsx';
import { ToolResultCard } from './ToolResultCard.tsx';
import { ToolResultsAccordion } from './ToolResultsAccordion.tsx';
import Button from '../../core/components/Button.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { url } from '../../core/utils/link.ts';
import { AUTOMATION_PATH } from '../constants.ts';
import './messageCursor.css';

const DELIVERY_RUN_ERRORS = ['FILE_NOT_GENERATED', 'MAILBOX_DISCONNECTED', 'EMAIL_FAILED', 'RECIPIENTS_FAILED'];

const CopyIcon = withIcon(faCopy);
const EditIcon = withIcon(faPen);
const RestartIcon = withIcon(faArrowRotateRight);
const ScheduleIcon = withIcon(faAlarmClock);
const WarningIcon = withIcon(faTriangleExclamation);
const ShareIcon = withIcon(faArrowUpFromBracket);
const BranchIcon = withIcon(faSplit);
const CheckIcon = withIcon(faCheck);

const SUPPORT_EMAIL = 'support@workist.com';

const CopySupportEmailButton = () => {
    const { t } = useTranslation('chat');
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        await navigator.clipboard?.writeText(SUPPORT_EMAIL);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div className="mt-3">
            <Button onClick={copy}>
                {copied ? <CheckIcon className="mr-2" /> : <CopyIcon className="mr-2" />}
                {t(copied ? 'thread.supportEmailCopied' : 'thread.copySupportEmail')}
            </Button>
        </div>
    );
};

const TOOL_RESULTS_COLLAPSE_THRESHOLD = 2;

// tools under a message: icon-only ghost buttons, 32px, shown on hover or keyboard focus (DESIGN.md); the latest
// answer keeps its row visible ("4.3 Answer actions")
const TOOL_BUTTON_CLASS =
    'flex h-8 w-8 cursor-pointer items-center justify-center rounded border-none bg-transparent text-sm text-content-medium transition-colors hover:bg-surface-strong hover:text-content-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong disabled:cursor-default disabled:opacity-50';
const TOOL_BAR_CLASS = 'mt-1 flex gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100';

const CreatedDocumentCard = ({ document }) => {
    const { t } = useTranslation('chat');
    const meta = CREATED_DOCUMENT_META[document.document_type] || CREATED_DOCUMENT_META.ORDER;
    const DocumentIcon = meta.icon;
    return (
        <div className="mt-2.5 flex max-w-md items-center gap-3 rounded-xl border border-solid border-line-weak bg-surface-weak p-3">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-surface-brand-medium-default text-content-brand-strong">
                <DocumentIcon />
            </span>
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-content-strong">{t(meta.createdKey)}</span>
                <span className="block truncate text-xs text-content-weak">
                    {document.document_number || document.file_name}
                    {document.channel_name ? ` · ${document.channel_name}` : ''}
                </span>
            </span>
            <Button variant="outline" asChild className="flex-none">
                <Link to={url(meta.path, { recordId: document.record_id })} target="_blank" rel="noreferrer">
                    {t('thread.openDocument')}
                </Link>
            </Button>
        </div>
    );
};

const ScheduleCreatedCard = ({ schedule }) => {
    const { t } = useTranslation('chat');
    // newer tool results carry the saved schedule's cadence fields themselves (the user may have
    // adjusted the proposal); older entries only have the call arguments
    const cadence = formatCadenceCompact(t, fromToolArgs(schedule.frequency ? schedule : schedule.args));
    return (
        <div className="mt-2.5 flex max-w-md items-center gap-3 rounded-xl border border-solid border-line-weak bg-surface-weak p-3">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-surface-brand-medium-default text-content-brand-strong">
                <ScheduleIcon />
            </span>
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-content-strong">
                    {t('thread.scheduleCreated')}
                </span>
                <span className="block truncate text-xs text-content-weak">
                    {schedule.name}
                    {cadence ? ` · ${cadence}` : ''}
                </span>
            </span>
            <Button variant="outline" asChild className="flex-none">
                <Link to={url(AUTOMATION_PATH, { scheduleId: schedule.schedule_id })}>{t('thread.viewSchedule')}</Link>
            </Button>
        </div>
    );
};

const WorkbenchLinkCard = ({ link }) => {
    const { t } = useTranslation('chat');
    const meta = CREATED_DOCUMENT_META[link.documentType] || CREATED_DOCUMENT_META.ORDER;
    const DocumentIcon = meta.icon;
    return (
        <div className="mt-2.5 flex max-w-md items-center gap-3 rounded-xl border border-solid border-line-weak bg-surface-weak p-3">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-surface-strong text-content-medium">
                <DocumentIcon />
            </span>
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-content-strong">{t(meta.workbenchKey)}</span>
                <span className="block truncate text-xs text-content-weak">{link.recordId}</span>
            </span>
            <Button variant="outline" asChild className="flex-none">
                <Link to={url(meta.path, { recordId: link.recordId })} target="_blank" rel="noreferrer">
                    {t('thread.openDocument')}
                </Link>
            </Button>
        </div>
    );
};

export const MessageBubble = ({
    message,
    conversationId = null,
    conversationAttachments = [],
    onAction = null,
    onEdit = null,
    onSchedule = null,
    onRetry = null,
    onPreviewAttachment = null,
    // sharing (ENG-11386): the owner shares the exchange a reply belongs to; a recipient of a shared snapshot
    // continues their own chat from a reply
    onShare = null,
    onContinueFrom = null,
    onRegenerate = null,
    feedback = null,
    actionsDisabled = false,
    isStreaming = false,
    onAdjustSchedule = null,
    unsent = false,
    optionsActive = false,
    chosenOption = null,
}) => {
    const { t } = useTranslation('chat');
    const isUser = message.role === 'user';
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(message.content);
    const createdDocuments = isUser ? [] : extractCreatedDocuments(message.toolCalls);
    const createdSchedules = isUser ? [] : extractCreatedSchedules(message.toolCalls);
    const {
        all: messageToolCalls,
        pending: pendingToolCall,
        generatedFiles: generatedFileToolCalls,
        trail: otherToolCalls,
    } = partitionToolCalls(isUser ? [] : message.toolCalls);
    const createdRecordIds = new Set(createdDocuments.map((document) => document.record_id?.toLowerCase()));
    // files generated by a tool (Excel, Word, PDF) render as a download card, so keep them out of the chip list
    const generatedAttachmentIds = isUser ? new Set<string>() : extractGeneratedAttachmentIds(message.toolCalls);
    const chipAttachments = (message.attachments || []).filter(
        (attachment) => !generatedAttachmentIds.has(attachment.id) && attachment.source !== RENDERED_CHART_SOURCE
    );
    const attachmentNames = new Set((message.attachments || []).map((attachment) => attachment.fileName));
    const workbenchLinks = isUser
        ? []
        : extractWorkbenchLinks(message.content).filter((link) => !createdRecordIds.has(link.recordId));
    const referencedEmailIds = isUser ? null : extractReferencedEmailIds(message.content);
    const charts = isUser || isStreaming ? [] : extractCharts(message.content);
    const suggestions = isUser ? [] : extractSuggestions(message.content);
    const options = hasDetailedOptions(suggestions) ? suggestions : [];

    const hasBubbleContent =
        isUser ||
        Boolean(message.content) ||
        chipAttachments.length > 0 ||
        charts.length > 0 ||
        generatedFileToolCalls.length > 0 ||
        otherToolCalls.length > 0 ||
        createdSchedules.length > 0;
    // messages posted by a scheduled run are not editable — editing would replay over the run history
    const isScheduledRun = Boolean(message.scheduleName);
    const canEdit = isUser && message.id && onEdit && !isScheduledRun;
    const canSchedule = isUser && message.id && onSchedule && !isScheduledRun;

    const saveEdit = () => {
        const next = draft.trim();
        if (!next) return;
        setEditing(false);
        onEdit(message.id, next);
    };

    const withStreamingCursor = (html: string) =>
        html.replace(/(<\/[a-zA-Z0-9]+>)\s*$/, '<span class="chat-cursor"></span>$1');

    const bubbleContent = (
        <>
            {isUser ? (
                message.content
            ) : (
                <div
                    className={MARKDOWN_STYLES}
                    dangerouslySetInnerHTML={{
                        __html: isStreaming
                            ? withStreamingCursor(renderMarkdown(message.content))
                            : renderMarkdown(message.content),
                    }}
                />
            )}
            {chipAttachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {chipAttachments.map((attachment) => (
                        <AttachmentChip
                            key={attachment.id}
                            attachment={attachment}
                            onClick={() => onPreviewAttachment?.(attachment)}
                            className={classnames(
                                isUser
                                    ? 'bg-surface-weak/20 text-content-white dark:text-content-black'
                                    : 'bg-surface-strong text-content-medium'
                            )}
                        />
                    ))}
                </div>
            )}
            {!isStreaming && (
                <>
                    {charts.map((spec, idx) => (
                        <MessageChart
                            key={idx}
                            spec={spec}
                            conversationId={conversationId}
                            messageId={message.id}
                            chartIndex={idx}
                            hasImage={attachmentNames.has(chartImageFileName(idx))}
                        />
                    ))}
                    {generatedFileToolCalls.map((toolCall) => (
                        <GeneratedFileCard
                            key={parseToolOutput(toolCall.tool_output).attachment_id}
                            toolCall={toolCall}
                            attachments={message.attachments || []}
                            onPreviewAttachment={onPreviewAttachment}
                        />
                    ))}
                    {otherToolCalls.length > TOOL_RESULTS_COLLAPSE_THRESHOLD ? (
                        <ToolResultsAccordion
                            toolCalls={otherToolCalls}
                            messageId={message.id}
                            messageToolCalls={messageToolCalls}
                            referencedEmailIds={referencedEmailIds}
                            conversationAttachments={conversationAttachments}
                            onAction={onAction}
                            onPreviewAttachment={onPreviewAttachment}
                            disabled={actionsDisabled}
                        />
                    ) : (
                        otherToolCalls.map((toolCall, idx) => (
                            <ToolResultCard
                                key={idx}
                                toolCall={toolCall}
                                messageId={message.id}
                                toolCallIndex={messageToolCalls.indexOf(toolCall)}
                                referencedEmailIds={referencedEmailIds}
                                conversationAttachments={conversationAttachments}
                                onAction={onAction}
                                onPreviewAttachment={onPreviewAttachment}
                                disabled={actionsDisabled}
                            />
                        ))
                    )}
                    {createdDocuments.map((document, idx) => (
                        <CreatedDocumentCard key={idx} document={document} />
                    ))}
                    {createdSchedules.map((schedule) => (
                        <ScheduleCreatedCard key={schedule.schedule_id} schedule={schedule} />
                    ))}
                    {workbenchLinks.map((link) => (
                        <WorkbenchLinkCard key={`${link.documentType}:${link.recordId}`} link={link} />
                    ))}
                </>
            )}
        </>
    );

    if (isUser) {
        return (
            <div className="group mb-5 flex flex-col items-end">
                {editing ? (
                    <div className="flex w-full max-w-[560px] flex-col gap-2 rounded-2xl rounded-tr-sm border border-solid border-line-strong bg-surface-weak p-2.5">
                        <textarea
                            autoFocus
                            rows={Math.min(8, draft.split('\n').length || 1)}
                            className="w-full resize-none border-none bg-transparent text-sm leading-relaxed text-content-strong outline-none"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    saveEdit();
                                }
                                if (e.key === 'Escape') setEditing(false);
                            }}
                        />
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" className="!px-3 !py-1 text-xs" onClick={() => setEditing(false)}>
                                {t('thread.cancelEdit')}
                            </Button>
                            <Button
                                variant="primary"
                                className="!px-3 !py-1 text-xs"
                                disabled={!draft.trim()}
                                onClick={saveEdit}
                            >
                                {t('thread.saveEdit')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="max-w-[560px] whitespace-pre-wrap rounded-2xl rounded-tr-sm bg-surface-brand-strong-default px-4 py-3 text-sm leading-relaxed text-content-white dark:text-content-black">
                            {bubbleContent}
                        </div>
                        {unsent && (
                            <div className="flex items-center gap-1.5 self-end text-xs text-content-medium">
                                <WarningIcon className="text-content-warning-strong" />
                                {t('thread.unsent')}
                            </div>
                        )}
                        {(canEdit || canSchedule) && !actionsDisabled && (
                            <div className={classnames(TOOL_BAR_CLASS, 'self-end')}>
                                <Tooltip content={t('thread.copyMessage')}>
                                    <button
                                        type="button"
                                        aria-label={t('thread.copyMessage')}
                                        onClick={() => navigator.clipboard?.writeText(message.content)}
                                        className={TOOL_BUTTON_CLASS}
                                    >
                                        <CopyIcon fixedWidth />
                                    </button>
                                </Tooltip>
                                {canEdit && (
                                    <>
                                        <Tooltip content={t('thread.editMessage')}>
                                            <button
                                                type="button"
                                                aria-label={t('thread.editMessage')}
                                                onClick={() => {
                                                    setDraft(message.content);
                                                    setEditing(true);
                                                }}
                                                className={TOOL_BUTTON_CLASS}
                                            >
                                                <EditIcon fixedWidth />
                                            </button>
                                        </Tooltip>
                                        <Tooltip content={t('thread.restartFromHere')}>
                                            <button
                                                type="button"
                                                aria-label={t('thread.restartFromHere')}
                                                onClick={() => onEdit(message.id, message.content)}
                                                className={TOOL_BUTTON_CLASS}
                                            >
                                                <RestartIcon fixedWidth />
                                            </button>
                                        </Tooltip>
                                    </>
                                )}
                                {onShare && (
                                    <Tooltip content={t('share.questionAndAnswer')}>
                                        <button
                                            type="button"
                                            aria-label={t('share.questionAndAnswer')}
                                            onClick={() => onShare(message)}
                                            className={TOOL_BUTTON_CLASS}
                                        >
                                            <ShareIcon fixedWidth />
                                        </button>
                                    </Tooltip>
                                )}
                                {canSchedule && (
                                    <Tooltip content={t('schedule.action')}>
                                        <button
                                            type="button"
                                            aria-label={t('schedule.action')}
                                            onClick={() => onSchedule(message)}
                                            className={TOOL_BUTTON_CLASS}
                                        >
                                            <ScheduleIcon fixedWidth />
                                        </button>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    const runFailed = message.scheduleRunStatus === 'failed';
    if (runFailed && !DELIVERY_RUN_ERRORS.includes(message.scheduleRunError)) return null;

    const replyActions = !isStreaming && message.id && message.status !== 'FAILED' && hasBubbleContent;

    return (
        <div className="group mb-5 flex flex-col items-start">
            <div className="flex w-full min-w-0 flex-col">
                {hasBubbleContent && <div className="text-sm leading-relaxed text-content-strong">{bubbleContent}</div>}
                {!isStreaming && message.content?.toLowerCase().includes(SUPPORT_EMAIL) && <CopySupportEmailButton />}
                {options.length > 0 && !isStreaming && (
                    <OptionCards
                        options={options}
                        chosen={chosenOption}
                        onChoose={optionsActive && !actionsDisabled ? onAction : null}
                    />
                )}
                {replyActions && (
                    <div className={classnames(TOOL_BAR_CLASS, 'self-start', feedback && 'opacity-100')}>
                        <Tooltip content={t('thread.copyMessage')}>
                            <button
                                type="button"
                                aria-label={t('thread.copyMessage')}
                                onClick={() => navigator.clipboard?.writeText(message.content)}
                                className={TOOL_BUTTON_CLASS}
                            >
                                <CopyIcon fixedWidth />
                            </button>
                        </Tooltip>
                        {onRegenerate && (
                            <Tooltip content={t('thread.regenerate')}>
                                <button
                                    type="button"
                                    aria-label={t('thread.regenerate')}
                                    disabled={actionsDisabled}
                                    onClick={() => onRegenerate(message)}
                                    className={TOOL_BUTTON_CLASS}
                                >
                                    <RestartIcon fixedWidth />
                                </button>
                            </Tooltip>
                        )}
                        {onShare && (
                            <Tooltip content={t('share.questionAndAnswer')}>
                                <button
                                    type="button"
                                    aria-label={t('share.questionAndAnswer')}
                                    onClick={() => onShare(message)}
                                    className={TOOL_BUTTON_CLASS}
                                >
                                    <ShareIcon fixedWidth />
                                </button>
                            </Tooltip>
                        )}
                        {feedback && (
                            <ConversationFeedback
                                conversationId={feedback.conversationId}
                                userRatingHelpful={feedback.userRatingHelpful}
                                buttonClassName={TOOL_BUTTON_CLASS}
                            />
                        )}
                        {onContinueFrom && (
                            <Tooltip content={t('share.continue')}>
                                <button
                                    type="button"
                                    aria-label={t('share.continue')}
                                    onClick={() => onContinueFrom(message)}
                                    className={TOOL_BUTTON_CLASS}
                                >
                                    <BranchIcon fixedWidth />
                                </button>
                            </Tooltip>
                        )}
                    </div>
                )}
                {pendingToolCall && !isStreaming && (
                    <PendingActionCard
                        messageId={message.id}
                        toolCall={pendingToolCall}
                        onAdjustSchedule={onAdjustSchedule}
                    />
                )}
                {message.status === 'CANCELLED' && (
                    <ChatAlert
                        className="mt-1.5 w-full"
                        severity="info"
                        text={t('thread.stopped')}
                        actions={
                            onRetry && (
                                <Button disabled={actionsDisabled} onClick={onRetry}>
                                    {t('thread.retry')}
                                </Button>
                            )
                        }
                    />
                )}
            </div>
        </div>
    );
};
